import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom, timeout } from 'rxjs/operators';

import * as Objects from '@core/objects';

import * as Search from '@app/models/search';
import { VersionFeedback } from '@core/models/version-feedback';
import * as formVFeedbackModel from '@core/models/version-feedback';

import { reportActionErrorToSentry } from '@core/services/utils/sentry-utils';

import { ApprovalsRequests } from './approvals.requests';

import * as fromStore from './approvals.state';
import * as fromActions from './approvals.actions';

@Injectable()
export class ApprovalsEffects {

    constructor(private actions$: Actions, private store$: Store<fromStore.Approvals>, private requests: ApprovalsRequests) {}


    public init$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.Init>(fromActions.ApprovalsActionType.Init),
        switchMap((action) => {
            let act: Array<any> = [
                new fromActions.LoadFilterLabels(),
                new fromActions.LoadResults(),
            ];

            //  load user filters if ownAccess is true and not "own"
            if (action.approvalsGlobalAccess && action.approvalsGlobalAccess !== 'own') {
                act.push(new fromActions.LoadFilterUsers());
            }

            return act;
        })
    ));

    public loadFilterLabels$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadFilterLabels>(fromActions.ApprovalsActionType.LoadFilterLabels),
        switchMap((action) => {
            return this.requests.filterLabels$().pipe(
                map((labels: Objects.Label[]) => {
                    return new fromActions.LoadFilterLabelsSuccess(labels);
                }),
                catchError((err: any) => {
                    reportActionErrorToSentry(err, {actionName: fromActions.ApprovalsActionType.LoadFilterLabels});
                    return of(new fromActions.LoadFilterLabelsError(err));
                })
            );
        })
    ));

    public loadFiltersLabelsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadFilterLabelsSuccess>(fromActions.ApprovalsActionType.LoadFilterLabelsSuccess),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        switchMap(([action, approvals]) => [
            new fromActions.PushSearchFilters(Search.SearchConvert.filtersFromParams(approvals.search_query_params, approvals.available_filters.labels)),
        ])
    ));

    public loadFilterUsers$: Observable<fromActions.ApprovalsActions> = createEffect(() =>
        this.actions$.pipe(
            ofType<fromActions.LoadFilterUsers>(fromActions.ApprovalsActionType.LoadFilterUsers),
            switchMap((action) =>
                this.requests.filterUsers$().pipe(
                    timeout(5000), // 5 seconds timeout
                    map((users: Objects.User[]) => {
                        return new fromActions.LoadFilterUsersSuccess(users);
                    }),
                    catchError((err: any) => {
                       
                        const isTimeout = err.name === 'TimeoutError';
                            
                        reportActionErrorToSentry(err, {actionName: fromActions.ApprovalsActionType.LoadFilterUsers});
                        return of(
                            isTimeout
                                ? new fromActions.LoadFilterUsersErrorNoResponse('No response within 5 seconds')
                                : new fromActions.LoadFilterUsersError(err)
                        );
                    })
                )
            )
        )
    );

    public LoadFilterUsersSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadFilterUsersSuccess>(fromActions.ApprovalsActionType.LoadFilterUsersSuccess),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        switchMap(([action, approvals]) => [
            new fromActions.PushSearchFilters(Search.SearchConvert.filtersFromParams(approvals.search_query_params, approvals.available_filters.users)),
        ])
    ));

    public setSearchTerm$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetSearchTerm>(fromActions.ApprovalsActionType.SetSearchTerm),
        switchMap((action) => [
            new fromActions.UpdateQueryParams()
        ])
    ));

    public applySearchFilter$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.ApplySearchFilter>(fromActions.ApprovalsActionType.ApplySearchFilter),
        switchMap((action) => [
            new fromActions.UpdateQueryParams()
        ])
    ));

    public removeSearchFilter$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.RemoveSearchFilter>(fromActions.ApprovalsActionType.RemoveSearchFilter),
        switchMap((action) => [
            new fromActions.UpdateQueryParams()
        ])
    )); 

    public updateQueryParams$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateQueryParams>(fromActions.ApprovalsActionType.UpdateQueryParams),
        withLatestFrom(this.store$.select(fromStore.searchSelector)),
        switchMap(([action, search]) => {
            return [
                new fromActions.SetQueryParams(Search.SearchConvert.paramsFromFilters(search.filters, search.term, null)),
            ]
        })
    ));

    public setQueryParams$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetQueryParams>(fromActions.ApprovalsActionType.SetQueryParams),
        switchMap((action) => {
            return [
            new fromActions.ResetFeedbacks(),
            new fromActions.LoadCompletedResults(),
            new fromActions.LoadUncompletedResults(),
        ]})
    ));

    public loadResults$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadResults>(fromActions.ApprovalsActionType.LoadResults),
        switchMap((action) => {
            return [
            new fromActions.LoadCompletedResults(),
            new fromActions.LoadUncompletedResults()
        ]})
    ));

    public loadMoreUncompletedResults$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadMoreUncompletedResults>(fromActions.ApprovalsActionType.LoadMoreUncompletedResults),
        switchMap((action) => {
            return [new fromActions.LoadUncompletedResults()]
        })
    ));

    public loadMoreCompletedResults$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadMoreCompletedResults>(fromActions.ApprovalsActionType.LoadMoreCompletedResults),
        switchMap((action) => {
            return [new fromActions.LoadCompletedResults()]
        })
    ));

    public reloadResults$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.ReloadResults>(fromActions.ApprovalsActionType.ReloadResults),
        switchMap((action) => {
            return [
            new fromActions.LoadResults()
        ]})
    ));

    public loadCompletedResultItems$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadCompletedResults>(fromActions.ApprovalsActionType.LoadCompletedResults),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        switchMap(([action, approvalsStore]) => {
            return this.requests.results$(Search.SearchConvert.removeVoidParams(approvalsStore.search_query_params),"completed", approvalsStore.results.next_page_completed, approvalsStore.search.limit, null).pipe(
                map((versions: Objects.Version[]) => {
                    return new fromActions.LoadCompletedResultsSuccess(versions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadCompletedResultsError(err));
                })
            );
        })
    ));

    public loadUncompletedResultItems$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadUncompletedResults>(fromActions.ApprovalsActionType.LoadUncompletedResults),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        switchMap(([action, approvalsStore]) => {
            return this.requests.results$(Search.SearchConvert.removeVoidParams(approvalsStore.search_query_params),"uncompleted", approvalsStore.results.next_page_uncompleted, approvalsStore.search.limit, null).pipe(
                map((versions: Objects.Version[]) => {
                    return new fromActions.LoadUncompletedResultsSuccess(versions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadUncompletedResultsError(err));
                })
            );
        })
    ));

    public loadUncompletedResultsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadUncompletedResultsSuccess>(fromActions.ApprovalsActionType.LoadUncompletedResultsSuccess),
        switchMap((action) => {
            let act = [];
            // call feedbacks only when version results are not empty
            if(action.versions.length > 0){
                act = [ new fromActions.LoadUncompletedResultsFeedbacks(action.versions) ];
            }            
            return act;
        })
    ));

    public loadCompletedResultsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadCompletedResultsSuccess>(fromActions.ApprovalsActionType.LoadCompletedResultsSuccess),
        switchMap((action) => {
            let act = [];
            // call feedbacks only when version results are not empty
            if(action.versions.length > 0){
                act = [ new fromActions.LoadCompletedResultsFeedbacks(action.versions) ];
            }            
            return act;
        })
    ));

    public loadUncompledResultFeedbacks$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadUncompletedResultsFeedbacks>(fromActions.ApprovalsActionType.LoadUncompletedResultsFeedbacks),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        switchMap(([action,approvalsStore]) => {
            if(approvalsStore.feedbacks.feedbackAccess != null && approvalsStore.feedbacks.feedbackAccess == false)
                return [];
            return this.requests.resultsFeedbacks$(action.versions).pipe(
                map((feedbacks: VersionFeedback[]) => {
                    // If feeds are empty, loadResults again (all feedbacks archived)
                    if(feedbacks.length === 0 ){
                        return new fromActions.ReloadResults();
                    }

                    // If there's exactly one action.var, check for pending feeds
                    if (action.versions.length === 1) {
                        const pendingFeeds = feedbacks.find((f: formVFeedbackModel.VersionFeedback) => f.status.status_code === formVFeedbackModel.FeedbackStatusCodeEnum.OPENED_CODE);//1
                
                        // If no pending feedbacks are found, loadResults (maybe version feedbacks are all completed)
                        if (!pendingFeeds) {
                            return new fromActions.ReloadResults();
                        }
                    }
                    // If there is feedbcaks pending feedbacks, just update feedbacks
                    return new fromActions.LoadUncompletedResultsFeedbacksSuccess(feedbacks);            
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadUncompletedResultsFeedbacksError(err));
                })
            );
        })
    ));

    public loadCompletedResultFeedbacks$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadCompletedResultsFeedbacks>(fromActions.ApprovalsActionType.LoadCompletedResultsFeedbacks),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        switchMap(([action,approvalsStore]) => {
            if(approvalsStore.feedbacks.feedbackAccess != null && approvalsStore.feedbacks.feedbackAccess == false)
                return [];
            return this.requests.resultsFeedbacks$(action.versions).pipe(
                map((feedbacks: VersionFeedback[]) => {
                    if(feedbacks.length == 0 ){
                        return new fromActions.ReloadResults(); // case when all feedbacks were archived
                    }
                    return new fromActions.LoadCompletedResultsFeedbacksSuccess(feedbacks);                  
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadCompletedResultsFeedbacksError(err));
                })
            );
        })
    ));


    public setVersionStatus$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetVersionStatus>(fromActions.ApprovalsActionType.SetVersionStatus),
        switchMap((action) => {
            return this.requests.setVersionStatus$(action.versionId, action.status).pipe(
                map((version: Objects.Version) => {
                    return new fromActions.SetVersionStatusSuccess(version);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetVersionStatusError(err));
                })
            );
          
        })
    ));

    // WS events 
    public updateVersion$: Observable<fromActions.ApprovalsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateVersion>(fromActions.ApprovalsActionType.UpdateVersion),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        mergeMap(([action, approvalsState]) => {
            let events = [];
            // check if version is on completed or uncompleted lists
            if (approvalsState.results.completed_items != null && approvalsState.results.completed_items.filter((version: Objects.Version) => version.id == action.versionId).length > 0){
                events.push(new fromActions.ReloadResults());
            } else if (approvalsState.results.uncompleted_items != null && approvalsState.results.uncompleted_items.filter((version: Objects.Version) => version.id == action.versionId).length > 0){
                events.push(new fromActions.ReloadResults());
            } 
            return events;
        })
    ));

    public updateVersionFeedbacks$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateVersionFeedbacks>(fromActions.ApprovalsActionType.UpdateVersionFeedbacks),
        withLatestFrom(this.store$.select(fromStore.approvalsSelector)),
        mergeMap(([action, approvalsState]) => {
            let events = [];
            // check if version is on completed or uncompleted lists to reload its feedbacks
            if (approvalsState.results.completed_items != null && approvalsState.results.completed_items.filter((version: Objects.Version) => version.id == action.versionId).length > 0){
                events.push(new fromActions.LoadCompletedResultsFeedbacks(approvalsState.results.completed_items.filter((version: Objects.Version) => version.id == action.versionId)));
            } else if (approvalsState.results.uncompleted_items != null && approvalsState.results.uncompleted_items.filter((version: Objects.Version) => version.id == action.versionId).length > 0){
                events.push(new fromActions.LoadUncompletedResultsFeedbacks(approvalsState.results.uncompleted_items.filter((version: Objects.Version) => version.id == action.versionId)));
            } else {
                // when version not in completed or uncompleted maybe its new, so reload results.
                events.push(new fromActions.ReloadResults());
            }
            return events;
        })
    ));

}
